/**
 * 画布全局键盘快捷键
 * - Delete/Backspace：删除选中元素与工作区圈选中的页面
 * - Space：画布平移模式（由外部控制 isSpacePressed 状态）
 * - Ctrl/Cmd + Z/Y：撤销/重做
 * - Ctrl/Cmd + C/X/V：复制/剪切/粘贴（V 键优先读系统剪贴板）
 * - Ctrl/Cmd + +/−：画布放大/缩小；Shift+0：100%；Shift+1：适应屏幕（可选）
 */

import { useEffect } from 'react';
import { WORKSPACE_ZOOM_MIN, WORKSPACE_ZOOM_MAX } from '@/components/canvas/shared/constants/legacyConfig';

interface UseKeyboardShortcutsOptions {
  selectedCount: number;
  deleteSelectedElements: () => void;
  undo: () => void;
  redo: () => void;
  copySelectedElements: (mode: 'copy' | 'cut') => boolean;
  cutSelectedElements: () => boolean;
  pasteFromSystemClipboard: () => Promise<boolean>;
  /** 空格平移（不传则由 useCanvasPointerMode 等模块自行处理） */
  onSpaceDown?: () => void;
  onSpaceUp?: () => void;
  /** Escape 键回调：用于退出组内编辑模式等 */
  onEscape?: () => void;
  /** 画布缩放快捷键（不传则不注册） */
  workspaceZoom?: {
    getZoom: () => number;
    setZoom: (z: number) => void;
    onZoomFit: () => void;
  };
}

const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');
const isCtrl = (e: KeyboardEvent) => (isMac ? e.metaKey : e.ctrlKey);

function isEditingInput(e: KeyboardEvent) {
  const active = document.activeElement;
  return (
    active?.getAttribute('contenteditable') === 'true' ||
    active?.tagName === 'INPUT' ||
    active?.tagName === 'TEXTAREA'
  );
}

export function useKeyboardShortcuts({
  selectedCount,
  deleteSelectedElements,
  undo,
  redo,
  copySelectedElements,
  cutSelectedElements,
  pasteFromSystemClipboard,
  onSpaceDown,
  onSpaceUp,
  onEscape,
  workspaceZoom,
}: UseKeyboardShortcutsOptions) {
  // ── Space (pan mode) ──
  useEffect(() => {
    if (!onSpaceDown && !onSpaceUp) return;
    const onDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat && !isEditingInput(e)) {
        e.preventDefault();
        onSpaceDown?.();
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) onSpaceUp?.();
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, [onSpaceDown, onSpaceUp]);

  // ── Escape ──
  useEffect(() => {
    if (!onEscape) return;
    const onDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isEditingInput(e)) {
        e.preventDefault();
        onEscape();
      }
    };
    window.addEventListener('keydown', onDown);
    return () => window.removeEventListener('keydown', onDown);
  }, [onEscape]);

  // ── Delete / Backspace ──
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (isEditingInput(e)) return;
      if (selectedCount > 0) {
        e.preventDefault();
        deleteSelectedElements();
      }
    };
    window.addEventListener('keydown', onDown);
    return () => window.removeEventListener('keydown', onDown);
  }, [selectedCount, deleteSelectedElements]);

  // ── Ctrl/Cmd + Z / Y / C / X / V；画布缩放（先于 Z 判断，避免与撤销冲突）──
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (isEditingInput(e)) return;
      const key = e.key.toLowerCase();

      if (workspaceZoom) {
        const clamp = (v: number) => Math.max(WORKSPACE_ZOOM_MIN, Math.min(WORKSPACE_ZOOM_MAX, v));
        const z = workspaceZoom.getZoom();
        if (
          isCtrl(e) &&
          !e.altKey &&
          (e.code === 'NumpadAdd' || e.code === 'Equal' || e.key === '+')
        ) {
          e.preventDefault();
          workspaceZoom.setZoom(clamp(z * 1.1));
          return;
        }
        if (
          isCtrl(e) &&
          !e.altKey &&
          (e.code === 'NumpadSubtract' || e.code === 'Minus' || e.key === '-')
        ) {
          e.preventDefault();
          workspaceZoom.setZoom(clamp(z / 1.1));
          return;
        }
        if (!isCtrl(e) && e.shiftKey && !e.altKey && e.code === 'Digit0') {
          e.preventDefault();
          workspaceZoom.setZoom(1);
          return;
        }
        if (!isCtrl(e) && e.shiftKey && !e.altKey && e.code === 'Digit1') {
          e.preventDefault();
          workspaceZoom.onZoomFit();
          return;
        }
      }

      if (isCtrl(e) && key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if (isCtrl(e) && ((e.shiftKey && key === 'z') || key === 'y')) { e.preventDefault(); redo(); return; }
      if (isCtrl(e) && key === 'c') {
        if (selectedCount > 0) { e.preventDefault(); copySelectedElements('copy'); }
        return;
      }
      if (isCtrl(e) && key === 'x') {
        if (selectedCount > 0) { e.preventDefault(); cutSelectedElements(); }
        return;
      }
      if (isCtrl(e) && key === 'v') {
        e.preventDefault();
        pasteFromSystemClipboard();
      }
    };
    window.addEventListener('keydown', onDown);
    return () => window.removeEventListener('keydown', onDown);
  }, [undo, redo, copySelectedElements, cutSelectedElements, pasteFromSystemClipboard, selectedCount, workspaceZoom]);
}
